import {
  WellCorrelationWidgetState,
  type WellboreRef,
  type WellCorrelationWidgetInitParameters,
} from '@go-widgets/well-logs-widget';
import type { GlobalConfig } from '@repo/go-ui';
import type {
  ReferenceDataTemplatesStore,
  ReferenceWellsChildrenStore,
  ReferenceWellsService,
  ReferenceWellsStore,
} from '@repo/reference-wells';
import { action, flow, makeObservable, observable, reaction, runInAction } from 'mobx';
import type { IReactionDisposer } from 'mobx';

import { lagacyAgent } from 'src/api/legacyAgent';
import { wellLogsWidgetAgent } from 'src/api/wellLogsWidgetAgent';
import type { TGroup, TWellListCombinedData } from 'src/entities/tab/TabEntity';
import type { IWidgetStore } from 'src/entities/widget/IWidgetStore';
import { requireService, requireServiceAccessor } from 'src/packages/di';
import { IS_PORTABLE_DEVICE } from 'src/packages/shared/consts/isPortableDevice';

import { setWellIdInGroup } from '../../utils/setWellIdInGroup';
import { getFilterCoordinator } from '../filter-coordinator/FilterCoordinator';
import type { TFilterState } from '../filter-coordinator/types';
import type { GroupSelectStore } from '../group-select/GroupSelect.store';

import { getCorrectLocale } from './utils';
import type { WellCorrelationWidgetEntity } from './WellCorrelationWidget.entity';

export type TCorrelationReferenceWellsDeps = {
  referenceWellsService: ReferenceWellsService;
  referenceWellsStore: ReferenceWellsStore;
  referenceDataTemplatesStore: ReferenceDataTemplatesStore;
  referenceWellsChildrenStore: ReferenceWellsChildrenStore;
};

/** Minimal consumer-tree shape used for mapping to WellboreRef (fact wellbores only). */
type TCorrelationReferenceConsumerTree = {
  wells: Array<{
    wellId: number;
    fact?: Array<{ wellboreId: number }>;
  }>;
};

/**
 * Map RW consumer tree → WellboreRef[] for correlation tablets.
 * Wells without concrete fact wellbores (empty / missing fact) are skipped entirely.
 */
export function mapReferenceTreeToWellboreRefs(tree: TCorrelationReferenceConsumerTree): WellboreRef[] {
  return tree.wells.flatMap((well) => {
    const fact = well.fact;
    if (!fact || fact.length === 0) {
      return [];
    }

    return fact.flatMap((wellbore) => {
      if (!Number.isFinite(wellbore.wellboreId)) {
        return [];
      }

      return [{ wellId: well.wellId, wellboreId: wellbore.wellboreId }];
    });
  });
}

export class WellCorrelationWidgetStore implements IWidgetStore {
  readonly referenceWellsService: ReferenceWellsService;
  readonly referenceWellsStore: ReferenceWellsStore;
  readonly referenceDataTemplatesStore: ReferenceDataTemplatesStore;
  readonly referenceWellsChildrenStore: ReferenceWellsChildrenStore;

  @observable isReady: boolean = false;
  @observable isGroupSelectOpen: boolean;
  @observable progress: number | null = null;
  @observable isReferenceWellsPanelOpen = false;
  @observable isExitFullscreenFetching: boolean = false;
  /** Applied group filter from FilterCoordinator (wells list + future reference-wells filtering). */
  @observable.ref appliedFilterState: TFilterState | null = null;

  private readonly wellCorrelationEntity: WellCorrelationWidgetEntity;
  private readonly groupSelectStore: GroupSelectStore;
  private readonly initParams: WellCorrelationWidgetInitParameters;
  private effectDisposers: IReactionDisposer[] = [];

  private lastConsumerTree: TCorrelationReferenceConsumerTree = { wells: [] };
  private childrenCatalogReady = false;
  private hasAppliedReferenceSelection = false;

  constructor(
    wellCorrelationEntity: WellCorrelationWidgetEntity,
    groupSelectStore: GroupSelectStore,
    private readonly wellListPanel: TWellListCombinedData,
    referenceWells: TCorrelationReferenceWellsDeps,
    private readonly theme = requireService('theme'),
    private readonly otherSettings = requireService('otherSettings'),
    private readonly stompCachePublisherService = requireService('stompCachePublisherService'),
    private readonly stompOperationsPublisherService = requireService('stompOperationsPublisherService'),
    private readonly wellStatusService = requireService('wellStatusService'),
    private readonly language = requireService('language'),
    private readonly wellLogsTemplateService = requireService('wellLogsTemplateService'),
    private readonly getAuthService = requireServiceAccessor('authService'),
    private readonly classOfUnitsService = requireService('classOfUnitsService'),
    private readonly operationsService = requireService('operationsService'),
    private readonly userPreferencesService = requireService('userPreference'),
    private readonly notifications = requireService('notifications'),
  ) {
    this.wellCorrelationEntity = wellCorrelationEntity;
    this.groupSelectStore = groupSelectStore;
    this.isGroupSelectOpen = wellCorrelationEntity.isGroupsDefaultOpened;

    this.referenceWellsService = referenceWells.referenceWellsService;
    this.referenceWellsStore = referenceWells.referenceWellsStore;
    this.referenceDataTemplatesStore = referenceWells.referenceDataTemplatesStore;
    this.referenceWellsChildrenStore = referenceWells.referenceWellsChildrenStore;

    const wellCorrelationTemplatesManager = this.wellLogsTemplateService.getTemplatesManager('LOG_DEPTH');

    this.initParams = {
      axiosInstance: wellLogsWidgetAgent,
      legacyAxiosInstance: lagacyAgent,
      stompPublisher: this.stompCachePublisherService.stompPublisher,
      wellDirectoryServiceStompPublisher: this.wellStatusService,
      templatesManager: wellCorrelationTemplatesManager.templatesManager,
      userSettingsManager: wellCorrelationTemplatesManager.userSettings,
      userData: {
        sub: this.getAuthService().userInfo.sub,
      },
      defaultDepthUnits:
        this.userPreferencesService.preferences.defaultDepthUnits === 'INDEX_DEPTH_SCALE_METERS' ? 'm' : 'ft',
      classOfUnitsService: this.classOfUnitsService,
      splitRange: {
        time: this.userPreferencesService.preferences.defaultSplitRange.time,
        depth: this.userPreferencesService.preferences.defaultSplitRange.depth,
      },
      operationsService: this.operationsService,
      operationsStompPublisher: this.stompOperationsPublisherService.stompPublisher,
    };

    this.lastConsumerTree = this.referenceWellsService.getSelectedTreeForConsumers();
    this.referenceWellsService.setOnSelectionChange((tree) => {
      this.lastConsumerTree = tree;
      this.tryApplyReferenceSelectionToWidget();
    });

    void this.referenceWellsChildrenStore.load().then(() => {
      this.childrenCatalogReady = true;
      this.tryApplyReferenceSelectionToWidget();
    });

    wellCorrelationEntity.setDisposeHostStore(() => this.dispose());

    makeObservable(this);
  }

  getReferenceWellsSnapshot(): unknown {
    return this.referenceWellsStore.getSettingsSnapshot();
  }

  @action.bound
  setReady(value: boolean): void {
    this.isReady = value;
  }

  @action.bound
  setIsGroupSelectOpen(value: boolean): void {
    this.isGroupSelectOpen = value;
  }

  @action.bound
  setGroupForWellCorrelation(group: TGroup | null): void {
    this.wellCorrelationEntity.setGroup(group ? group.id : null);
  }

  @action.bound
  setReferenceWellsPanelOpen(isOpen: boolean): void {
    this.isReferenceWellsPanelOpen = isOpen;
  }

  @action.bound
  toggleReferenceWellsPanel(): void {
    this.isReferenceWellsPanelOpen = !this.isReferenceWellsPanelOpen;
  }

  private tryApplyReferenceSelectionToWidget(): void {
    const state = this.wellCorrelationEntity.state;
    if (!state || state.status !== 'ready' || !this.childrenCatalogReady) {
      return;
    }

    const refs = mapReferenceTreeToWellboreRefs(this.lastConsumerTree);

    // Selection has wells but none expose fact wellbores — do not wipe / push empty tablets.
    if (refs.length === 0 && this.lastConsumerTree.wells.length > 0) {
      return;
    }

    if (refs.length === 0 && !this.hasAppliedReferenceSelection) {
      return;
    }

    state.updateWellbores(refs);
    this.hasAppliedReferenceSelection = true;
  }

  private getNewState(wellId: number) {
    const appParams = {
      wellId,
      width: 520,
      height: 500,
      locale: getCorrectLocale(this.language.language),
      theme: this.theme.theme,
      isAltNames: this.userPreferencesService.preferences.isAltNames,
      fpsLock: this.otherSettings.graphicsPerformanceFps,
      defaultSelectWellbore: this.userPreferencesService.preferences.autoSelectWellboreForDepth,
      fullScreen: this.wellCorrelationEntity.isFullscreen,
    } as const;

    return new WellCorrelationWidgetState(appParams, this.initParams, this.wellCorrelationEntity.stateParams, {
      onInitializedSuccess: () => {
        this.setReady(true);
        this.tryApplyReferenceSelectionToWidget();
      },
      onInitializationFailed: (_: string, error?: unknown) => {
        this.setReady(false);
        console.error(error);
        this.notifications.showErrorMessageT('errors:failedToInitWidget');
      },
      onInternalStateChanged: (state) => {
        this.wellCorrelationEntity.setStateParams(state);
      },
      onEditModeChanged: (state) => {
        this.wellCorrelationEntity.setEditMode(state);
      },
    });
  }

  effect = () => {
    void this.referenceDataTemplatesStore.loadTemplatesForWell(this.wellCorrelationEntity.wellId);

    const disposeGroupId = reaction(
      () => this.wellCorrelationEntity.groupId,
      (groupId) => {
        if (groupId === null) return;

        const widgets = this.groupSelectStore.groupManager.getWidgetsByGroupId(groupId);
        setWellIdInGroup(widgets, this.wellCorrelationEntity, this.wellListPanel);
      },
    );

    const disposeWellId = reaction(
      () => this.wellCorrelationEntity.wellId,
      (wellId, prevWellId) => {
        if (wellId && !this.wellCorrelationEntity.state) {
          const newState = this.getNewState(wellId);
          this.wellCorrelationEntity.setState(newState);
        } else if (wellId && this.wellCorrelationEntity.state && typeof prevWellId !== 'undefined') {
          this.hasAppliedReferenceSelection = false;
          this.wellCorrelationEntity.state.setAppParams({ wellId });
        } else if (!wellId && this.wellCorrelationEntity.state) {
          this.wellCorrelationEntity.state.destroy();
          this.wellCorrelationEntity.setState(null);
          this.setReady(false);
          this.hasAppliedReferenceSelection = false;
        }
      },
      { fireImmediately: true },
    );

    const disposeWellIdChange = reaction(
      () => this.wellCorrelationEntity.wellId,
      (wellId) => {
        this.referenceWellsService.setPrimaryWellId(wellId);
        void this.referenceDataTemplatesStore.reloadForWellChange(wellId);
      },
    );

    const disposeStateStatus = reaction(
      () => this.wellCorrelationEntity.state?.status,
      (status) => {
        if (status === 'ready') {
          this.tryApplyReferenceSelectionToWidget();
        }
      },
    );

    const disposeStateParams = reaction(
      () => ({
        theme: this.theme.theme,
        language: this.language.language,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
        fpsLock: this.otherSettings.graphicsPerformanceFps,
        defaultSelectWellbore: this.userPreferencesService.preferences.autoSelectWellboreForDepth,
      }),
      ({ theme, language, isAltNames, fpsLock, defaultSelectWellbore }) => {
        this.wellCorrelationEntity.state?.setAppParams({
          theme,
          locale: getCorrectLocale(language),
          isAltNames,
          fpsLock,
          defaultSelectWellbore,
        });
      },
      { fireImmediately: true },
    );

    const wellListPanelDisposer = reaction(
      () => this.wellListPanel.store.selectedWellId,
      (wellId) => {
        if (this.wellListPanel.panelState?.groupId === this.wellCorrelationEntity.groupId) {
          this.wellCorrelationEntity.setWellId(wellId);
        }
      },
      { fireImmediately: true },
    );

    const disposeAppliedFilter = reaction(
      () => getFilterCoordinator(this.groupSelectStore).getState(this.wellCorrelationEntity.groupId),
      (state) => {
        runInAction(() => {
          this.appliedFilterState = state;
        });
      },
      { fireImmediately: true },
    );

    this.effectDisposers = [
      disposeGroupId,
      disposeWellId,
      disposeWellIdChange,
      disposeStateStatus,
      disposeStateParams,
      wellListPanelDisposer,
      disposeAppliedFilter,
    ];

    return () => {
      this.effectDisposers.forEach((d) => d());
      this.effectDisposers = [];
    };
  };

  @action.bound
  enableEditMode(): void {
    if (IS_PORTABLE_DEVICE) return;

    const state = this.wellCorrelationEntity.state?.enterEditMode();
    if (state) {
      this.wellCorrelationEntity.setFullscreen(true);
    }
  }

  @flow.bound
  async *exitFullScreen(_themeConfig: GlobalConfig) {
    if (this.isExitFullscreenFetching) return;

    this.isExitFullscreenFetching = true;

    const enableToClose = await this.wellCorrelationEntity.state?.exitEditModeAsync();

    yield;

    if (enableToClose) {
      this.wellCorrelationEntity.setFullscreen(false);
    }

    this.isExitFullscreenFetching = false;
  }

  @action.bound
  dispose(): void {
    this.effectDisposers.forEach((d) => d());
    this.effectDisposers = [];
    this.referenceWellsChildrenStore.dispose();
    this.referenceWellsService.dispose();

    const storeService = requireService('widgetStoreService');
    storeService.removeStore(this.wellCorrelationEntity);
  }
}
